import {
  DateTimeCell,
  useIsTimeVisibleForColumn,
} from '@/components/DateTimeCell';
import { FileNameSeparatorWithExtension } from '@/components/FileNameSeparatorWithExtension';
import type { CancelRecord } from '@/features/invoice_reports/components/ConfirmCancelModal/context/ConfirmCancelModalContext';
import { SingleDeleteRecord } from '@/features/invoice_reports/components/ConfirmDeleteModal';
import type { DuplicateParams } from '@/features/invoice_reports/components/ConfirmDuplicateModal/context/ConfirmDuplicateModalContext';
import {
  ActionCell,
  ActionCellRecord,
  InfoTagCell,
  StatusCell,
  TooltipCell,
} from '@/features/invoice_reports/components/List/Table/Cells';
import {
  RESIZABLE_MIN_WIDTH,
  SORT_DIRECTIONS,
} from '@/features/invoice_reports/constants';
import { InvoiceReportColumn } from '@/features/invoice_reports/types';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  Checkbox,
  IconDrawer,
  TableProps,
  Tag,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { SearchedInvoiceReportPaymentStatus } from 'ap-openapi';
import classnames from 'classnames/bind';
import { format } from 'date-util';
import { useCallback, useMemo } from 'react';
import { isResizable } from '../helpers/resizableConfig';
import { getColumnWidth } from '../helpers/tableWidthCalculator';
import type { MultiColumnSortState } from '../types';
import styles from './columns.module.scss';

const cx = classnames.bind(styles);

export type UseColumnsProps = {
  selectedRowKeys: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (record: InvoiceReportColumn, checked: boolean) => void;
  isAllSelected: boolean;
  isAllDisabled: boolean;
  isLoading?: boolean;
  columnWidths?: Record<string, number>;
  sortOrders?: MultiColumnSortState[];
  canAgentEntry?: boolean;
  onDelete?: (record: SingleDeleteRecord) => void;
  onCancel?: (record: CancelRecord) => void;
  onDuplicate?: (params: DuplicateParams) => Promise<void>;
  onShare?: (record: ActionCellRecord) => void;
};

type Column = TableProps<InvoiceReportColumn>['columns'][number];
type ColumnWithResizableMin = Column & { resizableMinWidth?: number };

export const useColumns = ({
  selectedRowKeys,
  onSelectAll,
  onSelectRow,
  isAllSelected,
  isAllDisabled,
  isLoading = false,
  columnWidths = {},
  sortOrders,
  canAgentEntry = false,
  onDelete,
  onCancel,
  onDuplicate,
  onShare,
}: UseColumnsProps): ColumnWithResizableMin[] => {
  const { t } = useTranslation();
  const container = useGlobalContainerRef();

  const isTimeVisibleForColumn = useIsTimeVisibleForColumn(sortOrders);

  const sorterConfig = useMemo(
    () =>
      !isLoading
        ? {
            sorter: true,
            sortDirections: SORT_DIRECTIONS,
            showSorterTooltip: {
              mouseEnterDelay: 0.5,
            },
          }
        : { showSorterTooltip: false },
    [isLoading]
  );

  const handleCheckboxChange = useCallback(
    (e: CheckboxChangeEvent, record: InvoiceReportColumn) => {
      e.stopPropagation();
      onSelectRow(record, e.target.checked);
    },
    [onSelectRow]
  );

  const getCheckboxTooltipTitle = useCallback(
    (record: InvoiceReportColumn, canSelect: boolean) => {
      if (canSelect) return '';
      if (record.delete_action.reason) return record.delete_action.reason;

      const statusDisplayName = record.is_waiting
        ? t('invoice_reports.table.columns.approving_application_status')
        : record.status?.display_name;

      return `${statusDisplayName}${t(
        'invoice_reports.table.columns.application_number_not_approvable'
      )}`;
    },
    [t]
  );

  const onStopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const numberColumnMessageTypes = [
    'report_edoc_scanner_message',
    'duplicate_reports_message',
  ];

  const invoiceFileMessageTypes = ['invoice_file_edoc_scanner_message'];

  const allColumns: ColumnWithResizableMin[] = [
    {
      title: (
        <Checkbox
          className={cx(`${isAllDisabled ? 'disabledCustomCheckbox' : ''}`)}
          indeterminate={selectedRowKeys.length > 0 && !isAllSelected}
          checked={isAllSelected}
          disabled={isAllDisabled || isLoading}
          onChange={(e) => onSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'select',
      key: 'select',
      fixed: 'left',
      onCell: () => ({
        onClick: onStopPropagation,
      }),
      render(_, record) {
        const canSelect = record.delete_action.allowed;
        const tooltipTitle = getCheckboxTooltipTitle(record, canSelect);
        return (
          <TooltipCell
            title={tooltipTitle}
            wrapperClassName={cx(styles['columnTooltip'])}
          >
            <Checkbox
              checked={selectedRowKeys.includes(record.id)}
              disabled={!canSelect || isLoading}
              className={cx(`${!canSelect ? 'disabledCustomCheckbox' : ''}`)}
              onChange={(e: CheckboxChangeEvent) =>
                handleCheckboxChange(e, record)
              }
            />
          </TooltipCell>
        );
      },
      align: 'center',
      width: `${styles['column_select_width']}px`,
      className: cx(styles['checkboxContainer']),
    },
    {
      title: t('invoice_reports.table.columns.application_number'),
      dataIndex: 'number',
      key: 'number',
      fixed: 'left',
      render(value, record) {
        return (
          <InfoTagCell
            errorMessages={record.error_messages ?? undefined}
            messageType={numberColumnMessageTypes}
          >
            <div className={cx('numberContainer')}>
              {value}
              <Tooltip
                title={t('open_preview')}
                getPopupContainer={container}
                overlayClassName={cx(styles['columnTooltip'])}
              >
                <div className={cx('iconWrapper')}>
                  <IconDrawer />
                </div>
              </Tooltip>
            </div>
            <StatusCell status={record.status} />
          </InfoTagCell>
        );
      },
      width: `${styles['column_number_width']}px`,
      ...sorterConfig,
      showSorterTooltip: {
        placement: 'right',
        ...(typeof sorterConfig.showSorterTooltip === 'object'
          ? sorterConfig.showSorterTooltip
          : {}),
      },
    },
    {
      title: t('invoice_reports.table.columns.created_date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) => (
        <DateTimeCell
          value={value}
          showTime={isTimeVisibleForColumn('created_at')}
        />
      ),
      width: `${styles['column_created_at_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('invoice_reports.table.columns.application_date'),
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      render: (value: string | null) => (
        <DateTimeCell
          value={value}
          showTime={isTimeVisibleForColumn('submitted_at')}
        />
      ),
      width: `${styles['column_submitted_at_width']}px`,
      ...sorterConfig,
    },
    ...(canAgentEntry
      ? [
          {
            title: t('invoice_reports.table.columns.applicant'),
            dataIndex: 'office_member',
            key: 'office_member',
            render(value: { name?: string; number?: string } | undefined) {
              if (!value) return '';

              const tooltipTitle = `${value.name ?? ''} (${
                value.number ?? ''
              })`;
              return (
                <TooltipCell
                  title={tooltipTitle}
                  wrapperClassName={cx(styles['columnTooltip'])}
                  className={cx(styles['singleLineTooltipCell'])}
                >
                  <div className={cx(styles['applicantContainer'])}>
                    <div className={cx(styles['applicantName'])}>
                      {value.name || ''}
                    </div>
                    <div className={cx(styles['applicantId'])}>
                      {value.number || ''}
                    </div>
                  </div>
                </TooltipCell>
              );
            },
            width:
              getColumnWidth('office_member', columnWidths) ??
              `${styles['column_office_member_width']}px`,
            resizable: isResizable('office_member'),
            resizableMinWidth: RESIZABLE_MIN_WIDTH,
          },
        ]
      : []),
    {
      title: t('invoice_reports.table.columns.title'),
      dataIndex: 'title',
      key: 'title',
      render(value) {
        return (
          <TooltipCell
            title={value}
            wrapperClassName={cx(styles['columnTooltip'])}
          >
            {value || ''}
          </TooltipCell>
        );
      },
      width:
        getColumnWidth('title', columnWidths) ??
        `${styles['column_title_width']}px`,
      resizable: isResizable('title'),
      resizableMinWidth: RESIZABLE_MIN_WIDTH,
    },
    {
      title: t('invoice_reports.table.columns.payee'),
      dataIndex: 'ap_payee_name',
      key: 'ap_payee_name',
      render(value) {
        return (
          <TooltipCell
            title={value}
            wrapperClassName={cx(styles['columnTooltip'])}
          >
            {value || ''}
          </TooltipCell>
        );
      },
      width:
        getColumnWidth('ap_payee_name', columnWidths) ??
        `${styles['column_ap_payee_name_width']}px`,
      resizable: isResizable('ap_payee_name'),
      resizableMinWidth: RESIZABLE_MIN_WIDTH,
    },
    {
      title: t('invoice_reports.table.columns.expense_booking_date'),
      dataIndex: 'book_date',
      key: 'book_date',
      render: (value) => format(value, 'YYYY/MM/DD'),
      width: `${styles['column_book_date_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('invoice_reports.table.columns.payment_due_date'),
      dataIndex: 'due_date',
      key: 'due_date',
      render: (value) => format(value, 'YYYY/MM/DD'),
      width: `${styles['column_due_date_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('invoice_reports.table.columns.total_amount'),
      dataIndex: 'total_amount',
      key: 'total_amount',
      render(value, record) {
        return (
          <>
            <div>{value === '-' ? '' : value || ''}</div>
            {record.foreign_currency_amount && (
              <div className={cx(styles['foreignCurrencyAmount'])}>
                {record.foreign_currency_amount}
              </div>
            )}
          </>
        );
      },
      width:
        getColumnWidth('total_amount', columnWidths) ??
        `${styles['column_total_amount_width']}px`,
      align: 'right',
      resizable: isResizable('total_amount'),
      resizableMinWidth: RESIZABLE_MIN_WIDTH,
    },
    {
      title: t('invoice_reports.table.columns.approver'),
      dataIndex: 'report_approval_names',
      key: 'report_approval_names',
      render(value) {
        return (
          <TooltipCell
            title={value}
            wrapperClassName={cx(styles['columnTooltip'])}
          >
            {value || ''}
          </TooltipCell>
        );
      },
      width:
        getColumnWidth('report_approval_names', columnWidths) ??
        `${styles['column_report_approval_names_width']}px`,
      resizable: isResizable('report_approval_names'),
      resizableMinWidth: RESIZABLE_MIN_WIDTH,
    },
    {
      title: t('invoice_reports.table.columns.approval_date'),
      dataIndex: 'approved_at',
      key: 'approved_at',
      render: (value: string | null) => (
        <DateTimeCell
          value={value}
          showTime={isTimeVisibleForColumn('approved_at')}
        />
      ),
      width: `${styles['column_approved_at_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('invoice_reports.table.columns.payment_date'),
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (value, record) => (
        <div className={cx('paymentDateContainer')}>
          <span>{format(value, 'YYYY/MM/DD')}</span>
          {record.payment_status ===
            SearchedInvoiceReportPaymentStatus.paid && (
            <Tag closable={false} outline={false}>
              {t('invoice_reports.table.columns.payment_date.paid_label')}
            </Tag>
          )}
        </div>
      ),
      width: `${styles['column_payment_date_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('invoice_reports.table.columns.attached_invoice'),
      dataIndex: 'invoice_file',
      key: 'invoice_file',
      render(value, record) {
        if (!value) return '';

        return (
          <div className={cx(styles['invoiceFileContainer'])}>
            <TooltipCell
              title={value.name}
              wrapperClassName={cx(styles['columnTooltip'])}
              className={cx(styles['singleLineTooltipCell'])}
            >
              <FileNameSeparatorWithExtension>
                {value.name}
              </FileNameSeparatorWithExtension>
            </TooltipCell>

            <InfoTagCell
              errorMessages={record.error_messages ?? undefined}
              messageType={invoiceFileMessageTypes}
              automaticStatusWaiting={record.automatic_status_waiting}
              timestampStatusCreating={record.timestamp_status_creating}
              receiptType={value.receipt_type}
            />
          </div>
        );
      },
      width: `${styles['column_invoice_file_width']}px`,
    },
    {
      render(_, record) {
        return (
          <ActionCell
            record={record}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onCancel={onCancel}
            onShare={onShare}
          />
        );
      },
      width: `${styles['column_action_width']}px`,
      ellipsis: false,
      align: 'center',
      fixed: 'right',
    },
  ];

  return allColumns;
};
